import { useState, useMemo } from "react";
import {
  ShieldCheck,
  UserPlus,
  KeyRound,
  AlertTriangle,
  Save,
} from "lucide-react";
import "./settings.css";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_URL;

/* =====================================================
   SETTINGS PAGE
===================================================== */
export default function Settings() {
  // 🔐 Auth data
  const role = localStorage.getItem("role"); // super-admin, admin, etc.
  const isSuperAdmin = role === "super-admin";

  const [activeTab, setActiveTab] = useState(
    isSuperAdmin ? "create" : "password"
  );

  // Tabs based on role
  const tabs = useMemo(() => {
    const list = [];

    if (isSuperAdmin) {
      list.push({
        key: "create",
        label: "Create User",
        icon: <UserPlus size={16} />,
      });
    }

    list.push({
      key: "password",
      label: "Change Password",
      icon: <KeyRound size={16} />,
    });

    return list;
  }, [isSuperAdmin]);

  return (
    <div className="page settings-page">
      {/* ================= HEADER ================= */}
      <div className="page-header">
        <h2>
          <ShieldCheck size={22} /> Settings
        </h2>
        <p>
          {isSuperAdmin
            ? "Administrative user management"
            : "Account security"}
        </p>
      </div>

      {/* ================= TABS ================= */}
      <div className="tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ================= CONTENT ================= */}
      {activeTab === "create" && isSuperAdmin && <CreateUser />}
      {activeTab === "password" && <ChangePassword />}
    </div>
  );
}

/* =====================================================
   CREATE USER (SUPER ADMIN ONLY)
===================================================== */
function CreateUser() {
  const [form, setForm] = useState({
    personID: "",
    loginID: "",
    password: "",
    usr_role: "basic-user",
  });

  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    if (!form.personID || !form.loginID || !form.password) {
      return alert("All fields are required");
    }

    if (form.password.length < 6) {
      return alert("Password must be at least 6 characters");
    }

    if (!window.confirm("Create this user?")) return;

    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/admin-user/create-user`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            personID: Number(form.personID),
            loginID: form.loginID,
            password: form.password,
            usr_role: form.usr_role,
          }),
        }
      );

      if (!res.ok) throw new Error();

      alert("✅ User created successfully");
      setForm({
        personID: "",
        loginID: "",
        password: "",
        usr_role: "basic-user",
      });
    } catch {
      alert("❌ Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <div className="card-header">
        <h3 className="card-title">
          <UserPlus size={18} /> Create New User
        </h3>
        <p className="card-subtitle">Super admin only</p>
      </div>

      <div className="card-body">
        <FormInput
          label="Person ID"
          type="number"
          value={form.personID}
          onChange={(v) => setForm({ ...form, personID: v })}
        />

        <FormInput
          label="Login ID (Email / Username)"
          value={form.loginID}
          onChange={(v) => setForm({ ...form, loginID: v })}
        />

        <FormInput
          label="Password"
          type="password"
          value={form.password}
          onChange={(v) => setForm({ ...form, password: v })}
        />

        <div className="form-group">
          <label>User Role</label>
          <select
            value={form.usr_role}
            onChange={(e) =>
              setForm({ ...form, usr_role: e.target.value })
            }
          >
            <option value="basic-user">Basic User</option>
            <option value="admin">Admin</option>
            <option value="super-admin">Super Admin</option>
            <option value="faculty">Faculty</option>
            <option value="exam">Exam</option>
            <option value="registry">Registry</option>
          </select>
        </div>

        <SecurityNote />
      </div>

      <div className="card-footer">
        <button className="btn btn-primary" disabled={loading}>
          <Save size={16} />
          {loading ? "Creating..." : "Create User"}
        </button>
      </div>
    </form>
  );
}

/* =====================================================
   CHANGE PASSWORD (ALL USERS)
===================================================== */
function ChangePassword() {
  const [form, setForm] = useState({
    loginID: "",
    newPassword: "",
    confirmPassword: "",
  });
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  // ✅ FIX: token must be read here
  const accessToken = localStorage.getItem("token");

  const submit = async (e) => {
  e.preventDefault();

  if (!form.loginID || !form.newPassword || !form.confirmPassword) {
    return alert("All fields are required");
  }

  if (form.newPassword.length < 8) {
    return alert("Password must be at least 8 characters");
  }

  if (form.newPassword !== form.confirmPassword) {
    return alert("Passwords do not match");
  }

  if (!window.confirm("Change password?")) return;

  setLoading(true);
  try {
    const res = await fetch(
      `${API_BASE_URL}/api/v1/admin-user/change-password/${encodeURIComponent(
        form.loginID
      )}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          new_password: form.newPassword,
        }),
      }
    );

    if (!res.ok) throw new Error();

    alert("✅ Password changed successfully");
    setForm({
      loginID: "",
      newPassword: "",
      confirmPassword: "",
    });
    // 🔐 Clear auth data
      localStorage.removeItem("access_token");
      localStorage.removeItem("loginID");
      localStorage.removeItem("role");
      localStorage.removeItem("person_id");
      
      // 🔁 Redirect to login
      navigate("/login", { replace: true });

  } catch {
    alert("❌ Failed to change password");
  } finally {
    setLoading(false);
  }
};


  
  return (
    <form className="card danger" onSubmit={submit}>
      <div className="card-header">
        <h3 className="card-title text-danger">
          <KeyRound size={18} /> Change Password
        </h3>
        <p className="card-subtitle">Keep your account secure</p>
      </div>

      <div className="card-body">
        <FormInput
          label="Login ID"
          value={form.loginID}
          onChange={(v) => setForm({ ...form, loginID: v })}
        />

        <FormInput
          label="New Password"
          type="password"
          value={form.newPassword}
          onChange={(v) => setForm({ ...form, newPassword: v })}
        />

        <FormInput
          label="Confirm Password"
          type="password"
          value={form.confirmPassword}
          onChange={(v) =>
            setForm({ ...form, confirmPassword: v })
          }
        />

        <SecurityNote />
      </div>

      <div className="card-footer">
        <button className="btn btn-danger" disabled={loading}>
          <Save size={16} />
          {loading ? "Updating..." : "Change Password"}
        </button>
      </div>
    </form>
  );
}

/* =====================================================
   SHARED COMPONENTS
===================================================== */
function FormInput({ label, type = "text", value, onChange }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
      />
    </div>
  );
}

function SecurityNote() {
  return (
    <div className="alert alert-warning">
      <AlertTriangle size={18} />
      <div>
        <strong>Security Notice</strong>
        <p>Passwords should be changed regularly and kept confidential.</p>
      </div>
    </div>
  );
}
